import React, { useContext } from 'react';
import { Mechanic, StateContext } from '../../StateContext';
import { secondsToHours, totalProjectHours } from '../Links/weekView';
import './hourOverviewPage.css';
import { useLanguage } from '../../Services/LanguageContext';

interface IProps {
    mechanic?: Mechanic;
}

const MechanicOverviewPage = React.forwardRef<HTMLDivElement, IProps>((props, ref) => {
    const { t } = useLanguage();
    const { loginData } = useContext(StateContext);

    if (!props.mechanic || !props.mechanic.links || props.mechanic.links.length === 0) {
        return (
            <div ref={ref} className='print-page'>
                <div className='print-header'>
                    <h1 className='print-title'>{t('noDataAvailable')}</h1>
                    <div className='print-project-code'>{t('ensureProjectData')}</div>
                </div>
            </div>
        );
    }

    const projectData = props.mechanic.links
        .filter(link => {
            const fullProject = loginData?.projects?.find(p => p.id === link.projectId);
            const allLinks = fullProject?.links ?? [];

            const myHours = totalProjectHours([link]);
            const otherLinks = allLinks.filter(l => l.mechanicId !== props.mechanic!.id);

            if (otherLinks.length === 0) return true;

            const highestOtherHours = Math.max(...otherLinks.map(l => totalProjectHours([l])));
            return myHours >= highestOtherHours;
        })
        .map(link => {
            const fullProject = loginData?.projects?.find(p => p.id === link.projectId);
            const allLinks = fullProject?.links ?? [link];

            const workedHours = totalProjectHours(allLinks);

            const projectTypes = fullProject?.types ?? [];
            const hasTypes = projectTypes.length > 0;
            const estimatedHours = hasTypes
                ? projectTypes.reduce((sum, pt) => sum + secondsToHours(pt.calculatedTimeInSeconds), 0)
                : secondsToHours((fullProject ?? link.project).estimatedTimeInSeconds ?? 0);

            const remainingHours = estimatedHours - workedHours;
            const meerwerkHours = secondsToHours(fullProject?.meerwerkSeconds ?? 0);
            const dhzHours = secondsToHours(fullProject?.dhzSeconds ?? 0);

            return {
                projectName: link.project.name,
                projectId: link.project.id,
                workedHours,
                estimatedHours,
                remainingHours,
                meerwerkHours,
                dhzHours,
                materialsDelivered: link.project.materialsDelivered,
            };
        });

    const totalWorked    = projectData.reduce((sum, p) => sum + p.workedHours, 0);
    const totalEstimated = projectData.reduce((sum, p) => sum + p.estimatedHours, 0);
    const totalRemaining = projectData.reduce((sum, p) => sum + p.remainingHours, 0);
    const totalMeerwerk  = projectData.reduce((sum, p) => sum + p.meerwerkHours, 0);
    const totalDhz       = projectData.reduce((sum, p) => sum + p.dhzHours, 0);

    return (
        <div ref={ref} className='print-page'>
            <div className='print-header'>
                <h1 className='print-title'>{props.mechanic.name}</h1>
                <div className='print-project-code'>{t('mechanic')} {t('hoursOverview')}</div>
            </div>

            <table className='print-table'>
                <thead>
                    <tr>
                        <th className='print-th' style={{ textAlign: 'left', paddingLeft: '8px' }}>{t('project')}</th>
                        <th className='print-th'>Materiaal</th>
                        <th className='print-th print-total-col'>{t('hours')}</th>
                        <th className='print-th print-total-col'>{t('result')}</th>
                        <th className='print-th print-total-col'>{t('meerwerk')}</th>
                        <th className='print-th print-total-col'>{t('dhzWork')}</th>
                    </tr>
                </thead>
                <tbody>
                    {projectData.map((project, index) => (
                        <tr key={index}>
                            <td className='print-td print-mechanic-name-col'>
                                {project.projectName}
                            </td>
                            <td className={`print-td print-materials-cell ${project.materialsDelivered ? 'delivered' : 'not-delivered'}`}>
                                {project.materialsDelivered ? 'Geleverd' : 'Niet geleverd'}
                            </td>
                            <td className='print-td print-total-cell'>
                                {project.workedHours.toFixed(2)}
                            </td>
                            <td className={`print-td print-total-cell ${project.remainingHours >= 0 ? 'positive' : 'negative'}`}>
                                {project.remainingHours >= 0 ? '+' : ''}{project.remainingHours.toFixed(2)}
                            </td>
                            <td className='print-td print-total-cell' style={{ color: '#1976d2' }}>
                                {project.meerwerkHours > 0 ? `+${project.meerwerkHours.toFixed(2)}` : '—'}
                            </td>
                            <td className='print-td print-total-cell' style={{ color: '#c62828' }}>
                                {project.dhzHours > 0 ? `+${project.dhzHours.toFixed(2)}` : '—'}
                            </td>
                        </tr>
                    ))}

                    <tr className='print-totals-row'>
                        <td className='print-td' style={{ textAlign: 'left', paddingLeft: '8px', fontWeight: 'bold' }}>
                            {t('total')}
                        </td>
                        <td className='print-td'></td>
                        <td className='print-td print-grand-total'>
                            {totalWorked.toFixed(2)}
                        </td>
                        <td className={`print-td print-grand-total ${totalRemaining >= 0 ? 'positive' : 'negative'}`}>
                            {totalRemaining >= 0 ? '+' : ''}{totalRemaining.toFixed(2)}
                        </td>
                        <td className='print-td print-grand-total' style={{ color: '#1976d2' }}>
                            {totalMeerwerk > 0 ? `+${totalMeerwerk.toFixed(2)}` : '—'}
                        </td>
                        <td className='print-td print-grand-total' style={{ color: '#c62828' }}>
                            {totalDhz > 0 ? `+${totalDhz.toFixed(2)}` : '—'}
                        </td>
                    </tr>
                </tbody>
            </table>

            <div className='print-summary'>
                <div className='print-summary-row'>
                    <span className='print-summary-label'>{t('hoursWorked')}:</span>
                    <span className='print-summary-value'>{totalWorked.toFixed(2)}</span>
                </div>
                <div className='print-summary-row'>
                    <span className='print-summary-label'>{t('hoursEstimated')}:</span>
                    <span className='print-summary-value'>{totalEstimated.toFixed(2)}</span>
                </div>
                <div className='print-summary-row print-summary-result'>
                    <span className='print-summary-label'>{t('result')}:</span>
                    <span className={`print-summary-value ${totalRemaining >= 0 ? 'positive' : 'negative'}`}>
                        {totalRemaining >= 0 ? '+' : ''}{totalRemaining.toFixed(2)}
                    </span>
                </div>
                {totalMeerwerk > 0 && (
                    <div className='print-summary-row' style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed #ccc' }}>
                        <span className='print-summary-label' style={{ color: '#1976d2' }}>
                            {t('meerwerk')}:
                        </span>
                        <span className='print-summary-value' style={{ color: '#1976d2' }}>
                            +{totalMeerwerk.toFixed(2)}
                        </span>
                    </div>
                )}
                {totalDhz > 0 && (
                    <div className='print-summary-row'>
                        <span className='print-summary-label' style={{ color: '#c62828' }}>
                            {t('dhzWork')}:
                        </span>
                        <span className='print-summary-value' style={{ color: '#c62828' }}>
                            +{totalDhz.toFixed(2)}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
});

export default MechanicOverviewPage;
